import React from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { DiagnosticTradeOption } from "../../types/diagnostic";

/**
 * Step one of a multi-trade diagnostic: which trades the business runs.
 *
 * It sits above the questions because it decides what the questions are — a
 * plumbing-and-roofing shop is asked ten fewer questions than a four-trade
 * one, and each against its own targets.
 *
 * The house `ToggleGroup` rather than hand-rolled buttons. It is the same
 * control this was imitating, and it carries the accessibility the imitation
 * had to be corrected into: a `role="group"` root, `aria-pressed` on every
 * item, and roving-focus arrow-key navigation. Selection is announced, not
 * conveyed by border colour alone.
 */
export const TradeSelector: React.FC<{
  isDisabled: boolean;
  onToggle: (key: string) => void;
  options: DiagnosticTradeOption[];
  selected: string[];
}> = ({ isDisabled, onToggle, options, selected }) => (
  <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <h3 className="text-base font-normal text-gray-900 dark:text-white">
      Trades this business operates
    </h3>
    <p className="mb-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
      Select every trade the business runs. Revenue is asked per trade and
      averaged into one stage; margins, tickets, and closing rates are read
      against each trade&apos;s own targets.
    </p>
    <ToggleGroup
      aria-label="Trades this business operates"
      className="flex-wrap justify-start gap-2"
      disabled={isDisabled}
      onValueChange={(next) => {
        // The group hands back its whole new array, but the selection is not
        // built from it: `toggleTrade` owns the canonical trade order and
        // keeps a recorded trade the current instrument no longer offers —
        // neither of which the group knows about. So take only *which* key
        // changed and let the existing rule apply it.
        const changed =
          next.find((key) => !selected.includes(key)) ??
          selected.find((key) => !next.includes(key));
        if (changed !== undefined) onToggle(changed);
      }}
      type="multiple"
      value={selected}
      variant="outline"
    >
      {options.map((option) => (
        <ToggleGroupItem
          className="h-auto rounded-lg border-gray-200 px-3.5 py-2 text-sm font-normal text-gray-500 shadow-none hover:bg-gray-50 hover:text-gray-900 data-[state=on]:border-gray-400 data-[state=on]:bg-gray-50 data-[state=on]:text-gray-900 dark:border-slate-700 dark:text-gray-400 dark:hover:bg-slate-700/40 dark:hover:text-white dark:data-[state=on]:border-slate-500 dark:data-[state=on]:bg-slate-700/50 dark:data-[state=on]:text-white"
          key={option.key}
          value={option.key}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  </section>
);

export default TradeSelector;
